import { Injectable } from '@angular/core';
import {
  GlobalStore,
  SessionStorageService,
} from '@maximizer/core/shared/data-access';
import {
  AbEntryService,
  ComposeStore,
  LeadService,
  OutlookService,
  OutlookStore,
} from '@maximizer/outlook/shared/data-access';
import {
  EntryType,
  OutlookAbEntryDetails,
  OutlookEmail,
  OutlookLeadSearch,
} from '@maximizer/outlook/shared/domain';
import {
  Observable,
  Subject,
  Subscription,
  catchError,
  finalize,
  forkJoin,
  of,
} from 'rxjs';

const SYNC_HEALTH_CHECK_INTERVAL = 5 * 60 * 1000;

@Injectable()
export class ComposeService {
  private readonly recipientsClassifiedSubject = new Subject<OutlookEmail[]>();
  readonly recipientsClassified$ =
    this.recipientsClassifiedSubject.asObservable();

  private readonly composeClearedSubject = new Subject<void>();
  readonly composeCleared$ = this.composeClearedSubject.asObservable();

  private readonly loadErrorSubject = new Subject<unknown>();
  readonly loadErrors$ = this.loadErrorSubject.asObservable();

  private outlookEmails: OutlookEmail[] = [];
  private emailSubscription?: Subscription;
  private fetchGeneration = 0;

  constructor(
    private readonly abentryService: AbEntryService,
    private readonly leadService: LeadService,
    private readonly outlookService: OutlookService,
    private readonly storage: SessionStorageService,
    private readonly globalStore: GlobalStore,
    private readonly outlookStore: OutlookStore,
    private readonly composeStore: ComposeStore,
  ) {}

  generateLocalId(
    entry: OutlookEmail,
    position?: string,
    index?: number,
  ): string {
    const actualPosition = position ?? entry.position ?? '';
    const suffix = index !== undefined ? `_${index}` : '';
    return `${entry.id}_${entry.emailAddress}_${actualPosition}${suffix}`;
  }

  async startRecipientTracking(): Promise<void> {
    this.composeStore.setLoading(true);

    this.emailSubscription ??= this.outlookService.emailChanges$
      .pipe(
        catchError(async (error) => {
          this.loadErrorSubject.next(error);
          return [];
        }),
        finalize(() => {
          this.composeStore.setLoading(false);
        }),
      )
      .subscribe(async (emails: OutlookEmail[]) => {
        // The read pane feeds the same emailChanges$ stream; this singleton
        // subscription must only react while a compose item is active.
        if (!this.outlookService.isCompose) return;
        await this.processComposeEmails(emails);
      });

    const item = this.outlookService.mailboxItem;
    if (this.outlookService.mailbox && item) {
      // Remove-then-add keeps exactly one handler per mailbox item, including
      // after item switches where the previous item's registration is gone.
      item.removeHandlerAsync(Office.EventType.RecipientsChanged, () =>
        item.addHandlerAsync(
          Office.EventType.RecipientsChanged,
          this.composeItemsChanged.bind(this),
        ),
      );
    }

    await this.outlookService.updateEmails();
  }

  async composeItemsChanged(): Promise<void> {
    if (!this.outlookService.mailboxItem) return;
    await this.outlookService.updateEmails();
  }

  resetComposeState(): void {
    this.composeStore.setLoading(false);
    this.outlookEmails = [];
    this.composeStore.updateInMaximizer([]);
    this.composeStore.updateNotInMaximizer([]);
    this.storage.remove('saveOnlyEntries');
    this.storage.remove(
      this.outlookService.storagePartitionKey + 'selectedEmail',
    );
    this.composeClearedSubject.next();
  }

  private async processComposeEmails(
    composeEmails: OutlookEmail[],
  ): Promise<void> {
    if (composeEmails.length === 0) {
      if (this.composeStore.saveOnlyEntries().length > 0) {
        this.composeStore.updateInMaximizer([]);
        this.composeStore.updateNotInMaximizer([]);
        this.outlookEmails = [];
        this.composeStore.setLoading(false);
        return;
      }
      this.resetComposeState();
      return;
    }

    const composeId = await this.outlookService.getComposeEmailId();
    this.composeStore.setComposeId(composeId);

    this.outlookEmails = [...composeEmails];
    this.fetchEmailsInMaximizer();
    this.checkSyncHealthIfStale();
  }

  private checkSyncHealthIfStale(): void {
    const config = this.outlookStore.syncConfiguration();
    if (!config?.id || !config.enabled) return;

    const lastChecked = this.outlookStore.syncHealthLastChecked();
    const isStale =
      lastChecked === null ||
      Date.now() - lastChecked > SYNC_HEALTH_CHECK_INTERVAL;

    if (isStale) {
      this.outlookStore.requestSyncHealthCheck();
    }
  }

  private fetchEmailsInMaximizer(): void {
    // Guards against a slow earlier lookup overwriting a newer classification.
    const generation = ++this.fetchGeneration;
    this.composeStore.setLoading(true);

    const emails = this.outlookEmails.map(
      (outlookEmail) => outlookEmail.emailAddress,
    );
    const requests: {
      abentries: Observable<OutlookAbEntryDetails[]>;
      leads?: Observable<OutlookLeadSearch[]>;
    } = {
      abentries: this.abentryService.getByEmails(emails).pipe(
        catchError((error) => {
          console.error('Error fetching AbEntries:', error);
          return of([]);
        }),
      ),
    };

    if (this.globalStore.session()?.user.rights?.lead?.read) {
      requests.leads = this.leadService.getByEmails(emails).pipe(
        catchError((error) => {
          console.error('Error fetching Leads:', error);
          return of([]);
        }),
      );
    }

    forkJoin(requests)
      .pipe(
        catchError(async (error) => {
          this.loadErrorSubject.next(error);
          return { abentries: [], leads: [] };
        }),
        finalize(() => {
          if (generation === this.fetchGeneration) {
            this.composeStore.setLoading(false);
          }
        }),
      )
      .subscribe(async (data) => {
        if (generation !== this.fetchGeneration) return;

        let maximizerEntries: OutlookEmail[] = this.mapAbEntriesToOutlookEmail(
          data.abentries,
        );
        const leadEntries: OutlookEmail[] = this.mapLeadsToOutlookEmail(
          data.leads,
        );

        if (leadEntries.length > 0) {
          maximizerEntries = maximizerEntries.concat(leadEntries);
        }

        this.classifyEmails(maximizerEntries);
      });
  }

  private classifyEmails(maximizerEntries: OutlookEmail[]): void {
    const inMaximizerEmails: OutlookEmail[] = [];
    const notInMaximizerEmails: OutlookEmail[] = [];

    const foundEmailAddresses = new Set(
      maximizerEntries.map((entry) => entry.emailAddress),
    );

    this.outlookEmails.forEach((outlookEmail) => {
      if (foundEmailAddresses.has(outlookEmail.emailAddress)) {
        const matchingEntries = maximizerEntries.filter(
          (entry) => entry.emailAddress === outlookEmail.emailAddress,
        );

        matchingEntries.forEach((foundEntry, index) => {
          const emailWithPosition = { ...foundEntry };
          emailWithPosition.position = outlookEmail.position;
          // Create unique localId using service method to ensure consistency and avoid collisions
          emailWithPosition.localId = this.generateLocalId(
            foundEntry,
            outlookEmail.position,
            index,
          );

          if (
            emailWithPosition.emailAddress &&
            !inMaximizerEmails.some(
              (entry) => entry.localId === emailWithPosition.localId,
            )
          ) {
            inMaximizerEmails.push(emailWithPosition);
          }
        });
      } else if (
        outlookEmail.emailAddress &&
        !notInMaximizerEmails.some(
          (entry) => entry.emailAddress === outlookEmail.emailAddress,
        )
      ) {
        notInMaximizerEmails.push(outlookEmail);
      }
    });

    this.composeStore.updateInMaximizer(inMaximizerEmails);
    this.composeStore.updateNotInMaximizer(notInMaximizerEmails);
    this.recipientsClassifiedSubject.next(inMaximizerEmails);
  }

  private mapAbEntriesToOutlookEmail(
    abEntries?: OutlookAbEntryDetails[],
  ): OutlookEmail[] {
    if (!abEntries) return [];

    const outlookEmailAddresses = this.outlookEmails.map(
      (email) => email.emailAddress,
    );

    return (
      abEntries.map((abEntry) => {
        const matchingEmailFromOutlook = abEntry.emails.find((abEntryEmail) =>
          outlookEmailAddresses.find(
            (outlookAddress) =>
              outlookAddress.toLocaleLowerCase() ===
              abEntryEmail.value.toLocaleLowerCase(),
          ),
        );

        let displayName = abEntry.name;

        if (abEntry.type.toLocaleLowerCase() === EntryType.Company) {
          displayName = abEntry.companyName;
        }
        return {
          id: abEntry.key,
          emailAddress: matchingEmailFromOutlook?.value.toLocaleLowerCase(),
          displayName: displayName,
          type: 'abentry',
          abentryType: abEntry.type.toLocaleLowerCase(),
          parentKey: abEntry.parentKey,
        } as OutlookEmail;
      }) ?? []
    );
  }

  private mapLeadsToOutlookEmail(lead?: OutlookLeadSearch[]): OutlookEmail[] {
    if (!lead) return [];
    return lead
      .filter((result) => result.status !== 2)
      .map((result) => {
        return {
          id: result.key,
          emailAddress: result.email.toLocaleLowerCase(),
          displayName: result.name,
          type: 'lead',
        };
      });
  }
}
